
import React, { useState, useEffect } from 'react';
import { useLocalization } from '../hooks/useLocalization';
import { Link, useNavigate } from 'react-router-dom';
import { LANGUAGES } from '../constants';
import type { Language } from '../types';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './auth/AuthModal';

interface FooterProps {
  onToggleLogs?: () => void;
  language?: Language;
  setLanguage?: (lang: Language) => void;
}

// CMS Footer types
interface CmsFooterConfig {
  companyName?: string;
  companyDescription?: string;
  columns?: { title: string; links: { label: string; url: string; isActive: boolean }[] }[];
  socialLinks?: { platform: string; url: string; icon: string }[];
  partnerLogos?: { imageUrl: string; altText: string; linkUrl?: string }[];
  copyrightText?: string;
  showNewsletter?: boolean;
  newsletterTitle?: string;
  newsletterPlaceholder?: string;
  contactPhone?: string;
  contactPhoneLabel?: string;
  paymentMethods?: (string | { id: string; label: string; isActive: boolean; iconType: 'preset' | 'custom'; customImageUrl?: string; linkUrl?: string; bgColor?: string; svgColor?: string })[];
}

// ─── DEFAULT content mirroring lumitours.com ───
const DEFAULT_FOOTER: CmsFooterConfig = {
  companyName: 'Lumi Tours',
  companyDescription: 'Premium paketresor till handplockade destinationer. Vi erbjuder skräddarsydda reseupplevelser med personlig service och kvalitetsgaranti.',
  columns: [
    {
      title: 'Företaget',
      links: [
        { label: 'Om oss', url: '/about', isActive: true },
        { label: 'Resevillkor', url: '/sv/terms-and-conditions', isActive: true },
        { label: 'Integritet & Säkerhet', url: '/sv/cookie-gdpr', isActive: true },
        { label: 'Avbokningsvillkor', url: '/sv/cancellation', isActive: true },
      ],
    },
    {
      title: 'Kontakt',
      links: [
        { label: 'info@lumitours.com', url: 'mailto:info@lumitours.com', isActive: true },
      ],
    },
  ],
  socialLinks: [
    { platform: 'facebook', url: 'https://www.facebook.com/lumitours', icon: 'facebook' },
    { platform: 'instagram', url: 'https://www.instagram.com/lumi.tour', icon: 'instagram' },
    { platform: 'youtube', url: 'https://www.youtube.com/channel/UCpiFZ9XR9909NF9C9wa0IYg', icon: 'youtube' },
    { platform: 'linkedin', url: 'https://www.linkedin.com/company/lumitours', icon: 'linkedin' },
  ],
  partnerLogos: [
    {
      imageUrl: 'https://cms.lumitours.com//b2cImages/kammar.png',
      altText: 'Kammarkollegiet Resegaranti',
      linkUrl: 'https://www.kammarkollegiet.se/vara-tjanster/resegaranti/hitta-reseaktorer-med-resegaranti',
    },
  ],
  paymentMethods: [
    { id: 'visa', label: 'Visa', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
    { id: 'mastercard', label: 'MasterCard', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
    { id: 'discover', label: 'Discover', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
    { id: 'amex', label: 'American Express', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
    { id: 'jcb', label: 'JCB', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
    { id: 'dinersclub', label: 'Diners Club', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
    { id: 'stripe', label: 'Stripe', isActive: true, iconType: 'preset' as const, bgColor: '#ffffff' },
  ],
  copyrightText: '© 2025 LUMITOURS AB | All Rights Reserved',
  contactPhone: '+46 8 507 812 20',
  contactPhoneLabel: 'Ring Oss',
  showNewsletter: true,
  newsletterTitle: 'Nyhetsbrev',
  newsletterPlaceholder: 'Din e-postadress',
};

const LockIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
  </svg>
);

const TerminalIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
  </svg>
);

// Social icon SVGs
const SocialIcon: React.FC<{ platform: string }> = ({ platform }) => {
  const p = platform.toLowerCase();
  if (p === 'facebook') return <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>;
  if (p === 'instagram') return <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>;
  if (p === 'linkedin') return <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>;
  if (p === 'twitter' || p === 'x') return <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
  if (p === 'youtube') return <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>;
  return <span className="text-sm">🔗</span>;
};

// Payment method SVG icons
const PaymentIcon: React.FC<{ method: string }> = ({ method }) => {
  const m = method.toLowerCase();
  const iconStyle = "h-7 w-auto";

  if (m === 'visa') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <path d="M293.2 348.7l33.4-195.8h53.4l-33.4 195.8zM540.7 157.3c-10.6-4-27.2-8.3-47.9-8.3-52.8 0-90 26.6-90.2 64.7-.3 28.2 26.5 43.9 46.8 53.3 20.8 9.6 27.8 15.8 27.7 24.4-.1 13.2-16.6 19.2-31.9 19.2-21.4 0-32.7-3-50.3-10.2l-6.9-3.1-7.5 43.8c12.5 5.5 35.6 10.2 59.6 10.5 56.2 0 92.7-26.3 93.1-67-.2-22.3-14-39.3-44.8-53.3-18.6-9.1-30.1-15.2-30-24.4 0-8.2 9.7-16.9 30.6-16.9 17.5-.3 30.1 3.5 40 7.5l4.8 2.3 7.3-42.5zM682.7 152.9h-41.3c-12.8 0-22.4 3.5-28 16.3l-79.4 179.5h56.2s9.2-24.2 11.3-29.5h68.6c1.6 6.9 6.5 29.5 6.5 29.5H724l-41.3-195.8zm-65.9 126c4.4-11.3 21.4-54.8 21.4-54.8-.3.5 4.4-11.4 7.1-18.8l3.6 17s10.3 47 12.4 56.6h-44.5zM232 152.9L179.8 284l-5.6-27.1c-9.7-31.2-39.9-65.1-73.7-82l47.9 171.6h56.6l84.2-193.6H232z" fill="#1a1f71" />
      <path d="M125 152.9H40.2l-.7 3.8c67.1 16.2 111.5 55.4 129.9 102.4L151.1 169c-3.2-12.5-12.6-16-26.1-16.1z" fill="#f9a533" />
    </svg>
  );

  if (m === 'mastercard') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <circle cx="330" cy="250" r="150" fill="#eb001b" />
      <circle cx="450" cy="250" r="150" fill="#f79e1b" />
      <path d="M390 130.7c38.1 30.2 62.5 77.1 62.5 129.3s-24.4 99.1-62.5 129.3c-38.1-30.2-62.5-77.1-62.5-129.3s24.4-99.1 62.5-129.3z" fill="#ff5f00" />
    </svg>
  );

  if (m === 'amex' || m === 'americanexpress') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <rect width="780" height="500" rx="40" fill="#2e77bc" />
      <path d="M327.3 249.2h-23.5l11.8-28.4zm168.8-43.8h-40.3l-22.8 26.9-21.5-26.9h-89.3l-9.3 21.9-9.5-21.9H260l-39.8 88.3h46.5l5.8-14h13.2l5.8 14h50.6v-10.7l4.5 10.7h26.2l4.5-10.9v10.9h125.7l15.3-16.4 14.3 16.4h54.3l-38.5-44.3z" fill="#fff" />
    </svg>
  );

  if (m === 'discover') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <rect width="780" height="500" rx="40" fill="#fff" />
      <rect x="2" y="2" width="776" height="496" rx="38" fill="none" stroke="#ddd" strokeWidth="2" />
      <path d="M380 310c-33.1 0-60-26.9-60-60s26.9-60 60-60 60 26.9 60 60-26.9 60-60 60z" fill="#f76f20" />
      <text x="390" y="260" textAnchor="middle" fill="#fff" fontSize="42" fontWeight="bold" fontFamily="Arial">D</text>
      <text x="170" y="270" fill="#333" fontSize="48" fontWeight="bold" fontFamily="Arial">DISCOVER</text>
    </svg>
  );

  if (m === 'jcb') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <rect width="780" height="500" rx="40" fill="#fff" />
      <rect x="350" y="80" width="120" height="340" rx="20" fill="#0e4c96" />
      <rect x="490" y="80" width="120" height="340" rx="20" fill="#e0232e" />
      <rect x="630" y="80" width="120" height="340" rx="20" fill="#01a74e" />
      <text x="410" y="280" textAnchor="middle" fill="#fff" fontSize="68" fontWeight="bold" fontFamily="Arial">J</text>
      <text x="550" y="280" textAnchor="middle" fill="#fff" fontSize="68" fontWeight="bold" fontFamily="Arial">C</text>
      <text x="690" y="280" textAnchor="middle" fill="#fff" fontSize="68" fontWeight="bold" fontFamily="Arial">B</text>
    </svg>
  );

  if (m === 'dinersclub' || m === 'diners') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <rect width="780" height="500" rx="40" fill="#fff" />
      <rect x="2" y="2" width="776" height="496" rx="38" fill="none" stroke="#ddd" strokeWidth="2" />
      <circle cx="390" cy="250" r="130" fill="none" stroke="#0d4d94" strokeWidth="20" />
      <line x1="390" y1="130" x2="390" y2="370" stroke="#0d4d94" strokeWidth="16" />
      <text x="390" y="440" textAnchor="middle" fill="#0d4d94" fontSize="32" fontWeight="bold" fontFamily="Arial">DINERS CLUB</text>
    </svg>
  );

  if (m === 'stripe') return (
    <svg className={iconStyle} viewBox="0 0 780 500" xmlns="http://www.w3.org/2000/svg">
      <rect width="780" height="500" rx="40" fill="#6772e5" />
      <path d="M370 207c0-13.3 10.9-18.4 29-18.4 25.9 0 58.6 7.9 84.5 21.9V138c-28.3-11.2-56.3-15.6-84.5-15.6-69.1 0-115 36.1-115 96.4 0 94.1 129.6 79.1 129.6 119.7 0 15.7-13.7 20.8-32.8 20.8-28.3 0-64.5-11.6-93.2-27.2v73.8c31.7 13.7 63.8 19.5 93.2 19.5 70.8 0 119.6-35 119.6-96.2.1-101.6-130.4-83.5-130.4-121.2z" fill="#fff" />
    </svg>
  );

  return <span className="text-xs text-gray-500">{method}</span>;
};

export const Footer: React.FC<FooterProps> = ({ onToggleLogs, language, setLanguage }) => {
  const { t, language: currentLang } = useLocalization();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [cmsFooter, setCmsFooter] = useState<CmsFooterConfig | null>(null);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch CMS footer config
  useEffect(() => {
    fetch('/api/cms/site-layout')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.success && data.layout?.footer) {
          setCmsFooter(data.layout.footer);
        }
      })
      .catch(() => {/* use defaults */ });
  }, []);

  const handleAdminClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setShowAuthModal(true);
    } else if (user?.role !== 'admin') {
      setShowAccessDenied(true);
      setTimeout(() => setShowAccessDenied(false), 3000);
    } else {
      navigate('/admin');
    }
  };

  // Merge CMS with defaults
  const footer = {
    ...DEFAULT_FOOTER,
    ...(cmsFooter || {}),
  };

  // Use CMS columns if populated AND language is Swedish, otherwise build localized defaults
  // CMS data is stored in Swedish only, so we skip it for non-Swedish languages
  const isSv = currentLang === 'sv';
  const langPrefix = currentLang === 'da' ? '/da' : (isSv ? '/sv' : '/en');
  const columns = (isSv && cmsFooter?.columns && cmsFooter.columns.length > 0) ? cmsFooter.columns : [
    {
      title: t('footer_company'),
      links: [
        { label: t('footer_about_us'), url: '/about', isActive: true },
        { label: t('footer_terms'), url: `${langPrefix}/terms-and-conditions`, isActive: true },
        { label: t('footer_privacy'), url: `${langPrefix}/cookie-gdpr`, isActive: true },
        { label: t('footer_cancellation'), url: `${langPrefix}/cancellation`, isActive: true },
      ],
    },
    {
      title: t('footer_contact'),
      links: [
        { label: 'info@lumitours.com', url: 'mailto:info@lumitours.com', isActive: true },
      ],
    },
  ];
  const socialLinks = (cmsFooter?.socialLinks && cmsFooter.socialLinks.length > 0) ? cmsFooter.socialLinks : DEFAULT_FOOTER.socialLinks!;
  const partnerLogos = (cmsFooter?.partnerLogos && cmsFooter.partnerLogos.length > 0) ? cmsFooter.partnerLogos : DEFAULT_FOOTER.partnerLogos!;
  const copyrightText = footer.copyrightText || DEFAULT_FOOTER.copyrightText!;
  const phone = footer.contactPhone || DEFAULT_FOOTER.contactPhone!;
  const phoneLabel = footer.contactPhoneLabel || DEFAULT_FOOTER.contactPhoneLabel!;
  const companyDescription = footer.companyDescription || DEFAULT_FOOTER.companyDescription!;
  const showNewsletter = footer.showNewsletter ?? DEFAULT_FOOTER.showNewsletter;

  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">

          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <span className="text-xl font-black text-white tracking-tight">
                LUMI <span className="text-white">TOURS</span>
              </span>
              <span className="block text-[10px] text-gray-500 uppercase tracking-[0.3em] font-medium -mt-1">Premium Travel</span>
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">{(isSv && cmsFooter?.companyDescription) ? cmsFooter.companyDescription : t('footer_company_desc')}</p>

            {/* Social Links */}
            <div className="flex gap-2">
              {socialLinks.map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-all duration-300 p-2.5 rounded-lg hover:bg-gray-800 hover:scale-110"
                  title={social.platform}
                >
                  <SocialIcon platform={social.platform} />
                </a>
              ))}
            </div>
          </div>

          {/* Dynamic CMS Columns */}
          {columns.map((col, ci) => (
            <div key={ci}>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.filter(l => l.isActive).map((link, li) => (
                  <li key={li}>
                    {link.url.startsWith('http') || link.url.startsWith('mailto:') || link.url.startsWith('tel:') ? (
                      <a href={link.url} target={link.url.startsWith('mailto:') || link.url.startsWith('tel:') ? undefined : '_blank'} rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors duration-200 flex items-center gap-2">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.url} className="text-sm text-gray-400 hover:text-white transition-colors duration-200">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Phone & Newsletter Column */}
          <div>
            {/* Phone */}
            {phone && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">{(isSv && cmsFooter?.contactPhoneLabel) ? cmsFooter.contactPhoneLabel : t('footer_customer_service')}</h3>
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-gray-400 hover:text-white transition-colors group">
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-800 group-hover:bg-blue-600/20 transition-colors">
                    <PhoneIcon />
                  </span>
                  <span className="text-sm font-medium">{phone}</span>
                </a>
              </div>
            )}

            {/* Newsletter */}
            {showNewsletter && (
              <div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">{(isSv && cmsFooter?.newsletterTitle) ? cmsFooter.newsletterTitle : t('footer_newsletter')}</h3>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={(isSv && cmsFooter?.newsletterPlaceholder) ? cmsFooter.newsletterPlaceholder : t('footer_email_placeholder')}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                  <button
                    disabled={subscribing}
                    onClick={async () => {
                      if (!email || !email.includes('@')) {
                        setSubscribeMessage({ type: 'error', text: currentLang === 'sv' ? 'Ange en giltig e-postadress' : 'Please enter a valid email' });
                        return;
                      }
                      setSubscribing(true);
                      setSubscribeMessage(null);
                      try {
                        const res = await fetch('/api/newsletter/subscribe', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email, language: currentLang, source: 'footer' })
                        });
                        const data = await res.json();
                        if (data.success) {
                          setSubscribeMessage({ type: 'success', text: data.alreadySubscribed
                            ? (currentLang === 'sv' ? 'Du är redan prenumerant!' : 'Already subscribed!')
                            : (currentLang === 'sv' ? 'Tack! Du är nu prenumerant.' : 'Thanks! You are subscribed.')
                          });
                          setEmail('');
                        } else {
                          setSubscribeMessage({ type: 'error', text: data.error || 'Subscription failed' });
                        }
                      } catch {
                        setSubscribeMessage({ type: 'error', text: currentLang === 'sv' ? 'Något gick fel. Försök igen.' : 'Something went wrong. Try again.' });
                      } finally {
                        setSubscribing(false);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-600/25 whitespace-nowrap"
                  >
                    {subscribing ? '...' : t('footer_subscribe')}
                  </button>
                </div>
                {subscribeMessage && (
                  <p className={`text-xs mt-2 ${subscribeMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {subscribeMessage.text}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Partner Logos & Payment Methods */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
            {/* Partner Logos (Kammarkollegiet etc.) */}
            {partnerLogos.map((logo, i) => {
              const img = (
                <img
                  key={i}
                  src={logo.imageUrl}
                  alt={logo.altText}
                  className="h-8 md:h-10 w-auto opacity-60 hover:opacity-100 transition-all duration-300"
                  style={{ maxWidth: '120px' }}
                />
              );
              return logo.linkUrl ? (
                <a key={i} href={logo.linkUrl} target="_blank" rel="noopener noreferrer" className="inline-block hover:scale-105 transition-transform duration-200">
                  {img}
                </a>
              ) : <span key={i} className="inline-block">{img}</span>;
            })}

            {/* Divider */}
            <div className="hidden md:block w-px h-8 bg-gray-700" />

            {/* Individual Payment Method Icons */}
            {(footer.paymentMethods || DEFAULT_FOOTER.paymentMethods || []).map((pm: any, i: number) => {
              // Backward compat: support both old string[] and new object[] format
              const isObj = typeof pm === 'object';
              const methodId = isObj ? pm.id : pm;
              const isActive = isObj ? pm.isActive !== false : true;
              const label = isObj ? pm.label : methodId;
              const iconType = isObj ? pm.iconType : 'preset';
              const customImageUrl = isObj ? pm.customImageUrl : undefined;
              const linkUrl = isObj ? pm.linkUrl : undefined;
              const bgColor = isObj ? (pm.bgColor || '#ffffff') : '#ffffff';
              const svgColor = isObj ? pm.svgColor : undefined;

              if (!isActive) return null;

              const icon = iconType === 'custom' && customImageUrl ? (
                <img src={customImageUrl} alt={label} className="h-7 w-auto" />
              ) : (
                <PaymentIcon method={methodId} />
              );

              const wrapper = (
                <div
                  key={`pay-${i}`}
                  className="flex items-center justify-center rounded-lg px-2.5 py-1.5 opacity-80 hover:opacity-100 transition-all duration-300 hover:scale-110"
                  title={label}
                  style={{ backgroundColor: bgColor, ...(svgColor ? { color: svgColor } : {}) }}
                >
                  {icon}
                </div>
              );

              return linkUrl ? (
                <a key={`pay-${i}`} href={linkUrl} target="_blank" rel="noopener noreferrer">
                  {wrapper}
                </a>
              ) : wrapper;
            })}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto py-5 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <p className="text-xs text-gray-500 font-medium tracking-wide">{copyrightText}</p>

            {/* Right Side Controls */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleAdminClick}
                className="flex items-center gap-1.5 text-gray-600 hover:text-white transition-colors group text-xs font-bold uppercase tracking-wider"
              >
                <LockIcon />
                <span>{t('admin_login')}</span>
              </button>

              {/* Language Selector */}
              {language && setLanguage && (
                <div className="flex items-center rounded-full p-1 bg-gray-800 border border-gray-700">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`px-3 py-1 text-[10px] font-bold rounded-full transition-all duration-200 uppercase
                              ${language === lang.code
                          ? 'bg-white text-gray-900'
                          : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }
                            `}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}

              {/* API Log Button */}
              {onToggleLogs && (
                <button
                  onClick={onToggleLogs}
                  className="flex items-center gap-1.5 text-gray-600 hover:text-amber-500 transition-colors group text-xs font-bold uppercase tracking-wider"
                  title={t('api_logs')}
                >
                  <TerminalIcon />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Access Denied Toast */}
      {showAccessDenied && (
        <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
          <div className="bg-red-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">
              {language === 'sv' ? 'Endast admin har tillgång' : 'Admin access required'}
            </span>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          if (user?.role === 'admin') {
            navigate('/admin');
          }
        }}
        initialMode="login"
      />
    </footer>
  );
};
